import { useInfiniteQuery } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { EventSchema } from '@casetrace/shared';
import type { Event } from '@casetrace/shared';
import { db } from '../lib/firebase';

const PAGE_SIZE = 30;

export interface EventsPage {
  events: Event[];
  /** Siste dokument i siden – brukes som cursor for neste side */
  lastDoc: QueryDocumentSnapshot | null;
}

async function fetchEventsPage(
  caseId: string,
  cursor: QueryDocumentSnapshot | undefined,
): Promise<EventsPage> {
  const ref = collection(db, 'cases', caseId, 'events');
  const constraints = cursor
    ? [orderBy('timestamp', 'asc'), startAfter(cursor), limit(PAGE_SIZE)]
    : [orderBy('timestamp', 'asc'), limit(PAGE_SIZE)];
  const q = query(ref, ...constraints);
  const snap = await getDocs(q);

  const events = snap.docs.map((doc) =>
    // Dokumenter er allerede validert server-side; id hentes fra Firestore-nøkkelen.
    EventSchema.parse({ ...doc.data(), id: doc.id }),
  );

  return { events, lastDoc: snap.docs[snap.docs.length - 1] ?? null };
}

/**
 * Henter hendelser for en sak inkrementelt fra Firestore.
 * Returnerer en TanStack Query infinite-query – kall fetchNextPage for å laste mer.
 *
 * Tung facet-filtrering og full-tekst-søk er delegert til Typesense (§11.2).
 * In-memory-filtrering i useCaseTimeline er tilstrekkelig for MVP.
 */
export function useCaseEvents(caseId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['events', caseId] as const,
    queryFn: ({ pageParam }: { pageParam: QueryDocumentSnapshot | undefined }) =>
      fetchEventsPage(caseId, pageParam),
    getNextPageParam: (lastPage: EventsPage) => lastPage.lastDoc ?? undefined,
    initialPageParam: undefined as QueryDocumentSnapshot | undefined,
    enabled: enabled && Boolean(caseId),
  });
}
